import { useTranslation } from 'react-i18next';
import React, { useContext, useState } from 'react';
import { BudgetExpenseOrIncome, BudgetSections } from 'business/constants';
import { useParams } from 'react-router-dom';
import TabActions from '../TabActions';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { Box, Button, Stack, Typography } from '@mui/material';
import styled from '@mui/system/styled';
import AuxiliaryTabs from 'components/SharedComponents/AuxiliaryTabs/AuxiliaryTabs';
import BudgetFilters from '../../shared/Filters/BudgetFilters';
import AnalysePivotGrid from '../../BudgetProposal/Resource/Analyse/components/AnalysePivotGrid';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import ExportMenuContainer from 'components/ExportMenu/ExportMenuContainer';
import BudgetCard from '../../BudgetProposal/shared/Card/BudgetCard';
import { translateBlockValue } from 'i18n';
import {
    FiltersByExpInc,
    FiltersBySectionByExpInc,
    useCreateFilters,
} from './helpers/queryHelpers';
import { groupBudgetProposalsBySection } from './helpers/dataHelpers';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import ArgumentsListModal from './components/ArgumentsListModal';
import { isCustomBudgetProposal } from '../../BudgetProposal/Resource/hooks/budgetProposal';
import {
    useGetBudgetUserSettings,
    useMutateBudgetUserSettings,
} from './hooks/budgetUserSettings';
import {
    useGetDafBudgetFilters,
    useMutateDafBudgetFilter,
} from './hooks/budgetOverviewFilters';
import {
    getBudgetUserSettingsFieldBySection,
    getSifacUpdateBudgetSettingsCallback,
    getUpdateBudgetUserSettingsCallback,
} from './helpers/settingsHelpers';
import { NO_SECTION, useExporterBySection } from './helpers/exportHelpers';
import { useGridFiltersHelper } from '../../BudgetProposal/Resource/Analyse/hooks/useGridFiltersHelper';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';
import { IntegrationContext } from 'routes/BudgetModule/components/BudgetWithProvider/IntegrationContext';

const StyGrid = styled(Box, {
    name: 'StyGrid',
})({
    padding: '0 3rem 3rem 3rem',
});

type AnalyseArgumentsProps = {
    budgetProposals: BudgetProposalT[];
    year: number;
    selectedBudgetName?: string | null;
};

type UrlParams = {
    tab: string;
    expenseOrIncome: 'expense' | 'income' | undefined;
};

/**
 * Renders the analysis and arguments tab with pivot grids and filters for budget proposals.
 *
 * @param budgetProposals - The array of budget proposals including both operating and investment.
 * @param year - The exercice for which the analysis is being done.
 * @param setFilters - A function to update the filters, used in budgetFilters.
 * @returns The rendered analysis and arguments component.
 */
export default function AnalyseArguments({
    budgetProposals,
    year,
    selectedBudgetName,
}: AnalyseArgumentsProps): React.ReactElement {
    const [exportersBySection, setExporterBySection] = useExporterBySection();

    const [showArgumentsListModal, setShowArgumentsListModal] = useState(false);
    const { t } = useTranslation();
    const firstDefaultBudgetProposal = budgetProposals[0];
    const { mutate: mutateFilter } = useMutateDafBudgetFilter(
        firstDefaultBudgetProposal.id,
    );

    const {
        tab,
        expenseOrIncome:
            expenseOrIncomeUrlParams = BudgetExpenseOrIncome.EXPENSE,
    } = useParams<UrlParams>();

    const { budgetUserSettings } = useGetBudgetUserSettings();
    const { filters } = useGetDafBudgetFilters(firstDefaultBudgetProposal.id);

    const expenseOrIncome =
        expenseOrIncomeUrlParams.toUpperCase() as BudgetExpenseOrIncome;
    const { gridFilters, updateAnalyseGridFilters: updateGridFilters } =
        useGridFiltersHelper(year);

    // SIFAC Adaptation
    const integrations = useContext(IntegrationContext);
    const isSchool = isCustomBudgetProposal(integrations);
    const budgetProposalsBySection = !isSchool
        ? groupBudgetProposalsBySection(budgetProposals)
        : budgetProposals;

    const pivotedFilters = useCreateFilters({
        budgetProposals: budgetProposalsBySection,
        filters,
        year,
    });

    const { mutate: mutateSettings } = useMutateBudgetUserSettings();

    const onArgumentsSearchButtonClick = () => {
        setShowArgumentsListModal(true);
        trackEventBudget(
            TRACKABLE_EVENTS.budget.budgetOverview.analyse_arguments
                .openArgumentModal,
        );
    };
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    const tabsList = [
        {
            label: t('budget.expense'),
            pathname: `/budget_overview/${tab}/expense`,
        },
        {
            label: t('budget.income'),
            pathname: `/budget_overview/${tab}/income`,
        },
    ].map((tabItem) => {
        return {
            ...tabItem,
            key: tabItem.label,
            tabActions: (
                <>
                    <TabActions
                        trackedEvents={
                            TRACKABLE_EVENTS.budget.budgetOverview
                                .analyse_arguments
                        }
                        toggleLabel={t(
                            'budgetProposal.hideEmptySecondaryBudgetAxisElements',
                            { context: sourceTable },
                        )}
                    />
                    <Button
                        variant="outlined"
                        sx={{ height: '26px', ml: 'auto', mr: '3rem' }}
                        startIcon={<FindInPageIcon />}
                        onClick={onArgumentsSearchButtonClick}
                    >
                        <Typography variant="body1SemiBold" color="inherit">
                            {t('analyseArguments.argumentsSearchButtonTitle')}
                        </Typography>
                    </Button>
                </>
            ),
            children: (
                <Stack spacing={6}>
                    <BudgetFilters
                        filters={filters}
                        updateFilter={mutateFilter}
                        budgetBy={firstDefaultBudgetProposal.by}
                    />
                    {!isSchool ? (
                        [
                            BudgetSections.OPERATING,
                            BudgetSections.INVESTMENT,
                        ].map((section: BudgetSections) => {
                            const currentGridsettings =
                                getBudgetUserSettingsFieldBySection(
                                    section,
                                    budgetUserSettings,
                                );
                            const updateBudgetUserSettings =
                                getUpdateBudgetUserSettingsCallback(
                                    section,
                                    mutateSettings,
                                );
                            return (
                                <div key={section}>
                                    <Typography
                                        sx={{
                                            textAlign: 'center',
                                            margin: '1rem',
                                        }}
                                        variant="h4"
                                        color="primary"
                                    >
                                        {translateBlockValue(section, true)}
                                    </Typography>
                                    <BudgetCard
                                        title={t(
                                            'budgetProposal.pivotTableYearlyComparisonNoDimensions',
                                            {
                                                section: translateBlockValue(
                                                    section,
                                                    true,
                                                ),
                                            },
                                        )}
                                        exportMenu={
                                            <ExportMenuContainer
                                                exportExcelData={
                                                    exportersBySection[section]
                                                }
                                            />
                                        }
                                        sx={{ width: '100%', margin: '1rem' }}
                                    >
                                        <AnalysePivotGrid
                                            filters={
                                                (
                                                    pivotedFilters as FiltersBySectionByExpInc
                                                )[section][expenseOrIncome]
                                            }
                                            updateGridFilters={
                                                updateGridFilters
                                            }
                                            gridFilters={gridFilters}
                                            onSettingsUpdate={
                                                updateBudgetUserSettings
                                            }
                                            selectedYear={year}
                                            budgetProposal={
                                                (
                                                    budgetProposalsBySection as Record<
                                                        BudgetSections,
                                                        BudgetProposalT[]
                                                    >
                                                )[section][0]
                                            }
                                            setExporterToExcel={(exporter) =>
                                                setExporterBySection(
                                                    exporter,
                                                    section,
                                                )
                                            }
                                            settings={
                                                currentGridsettings.columns
                                            }
                                        />
                                    </BudgetCard>
                                </div>
                            );
                        })
                    ) : (
                        <div>
                            <BudgetCard
                                title={t(
                                    'budgetProposal.pivotTableYearlyComparisonSifac',
                                )}
                                exportMenu={
                                    <ExportMenuContainer
                                        exportExcelData={
                                            exportersBySection[NO_SECTION]
                                        }
                                    />
                                }
                                sx={{ width: '100%', margin: '1rem' }}
                            >
                                <AnalysePivotGrid
                                    filters={
                                        (pivotedFilters as FiltersByExpInc)[
                                            expenseOrIncome
                                        ]
                                    }
                                    updateGridFilters={updateGridFilters}
                                    gridFilters={gridFilters}
                                    onSettingsUpdate={getSifacUpdateBudgetSettingsCallback(
                                        mutateSettings,
                                    )}
                                    selectedYear={year}
                                    budgetProposal={firstDefaultBudgetProposal}
                                    setExporterToExcel={(exporter) =>
                                        setExporterBySection(exporter)
                                    }
                                    settings={
                                        budgetUserSettings.analyseDafCommonGrid
                                            .columns
                                    }
                                />
                            </BudgetCard>
                        </div>
                    )}
                </Stack>
            ),
        };
    });

    return (
        <StyGrid>
            <AuxiliaryTabs
                tabsList={tabsList}
                sx={{ '& > #auxiliary-tab-header': { pl: 2 } }}
            />
            <ArgumentsListModal
                isOpen={showArgumentsListModal}
                onClose={() => setShowArgumentsListModal(false)}
                exercice={year}
                budgetNames={selectedBudgetName}
            />
        </StyGrid>
    );
}
